import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import type {} from "@mui/lab/themeAugmentation";
import type { PaletteOptions } from "@mui/material";
import { alpha } from "@mui/material";
import type { ThemeOptions, TypographyVariantsOptions } from "@mui/material/styles";
import type {} from "@mui/x-charts/themeAugmentation";
import type {} from "@mui/x-data-grid-premium/themeAugmentation";
import type {} from "@mui/x-date-pickers-pro/themeAugmentation";
import React from "react";
import CollapseIcon from "./icons/CollapseIcon";
import ExpandIcon from "./icons/ExpandIcon";

declare module "@mui/material/styles" {
  interface TypeBackground {
    grey: string;
  }

  interface PaletteOptions {
    tooltip: {
      fill: string;
    };
    DataGrid: {
      headerBg: string;
    };
    subtleSurfaceBg: string;
  }

  interface Palette {
    tooltip: {
      fill: string;
    };
    DataGrid: {
      headerBg: string;
    };
    subtleSurfaceBg: string;
  }
}

const lightPaletteColors: PaletteOptions = {
  primary: {
    main: "#00A866",
    dark: "#009066",
    light: "#32CA7F",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#647381",
    dark: "#303A48",
    light: "#8E9BA9",
    contrastText: "#FFFFFF",
  },
  action: {
    active: "#8E9BA9",
    hover: "rgba(35, 52, 59, 0.04)",
    selected: "#EEEFF0",
    disabledBackground: "#F5F5F5",
    focus: "rgba(100, 115, 129, 0.12)",
    disabled: "#C9CFD6",
  },
  text: {
    primary: "#1F2329",
    secondary: "#576572",
    disabled: "#B4BDC6",
  },
  info: {
    main: "#0288D1",
    dark: "#01579B",
    light: "#03A9F4",
    contrastText: "#FFFFFF",
  },
  error: {
    main: "#D32F2F",
    dark: "#C62828",
    light: "#EF5350",
    contrastText: "#FFFFFF",
  },
  warning: {
    main: "#ED6C02",
    dark: "#E65100",
    light: "#FF9800",
    contrastText: "#FFFFFF",
  },
  success: {
    main: "#2E7D32",
    dark: "#1B5E20",
    light: "#4CAF50",
    contrastText: "#FFFFFF",
  },
  background: {
    default: "#FFFFFF",
    paper: "#FFFFFF",
    grey: "#FAFAFA",
  },
  divider: "#E5E6E9",
  common: {
    black: "#000000",
    white: "#FFFFFF",
  },
  tooltip: {
    fill: "#23343B",
  },
  DataGrid: {
    headerBg: "#FAFAFA",
  },
  subtleSurfaceBg: "#F6F8FA",
};

const darkPaletteColors: PaletteOptions = {
  primary: {
    main: "#00A866",
    dark: "#009066",
    light: "#32CA7F",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "rgba(217, 237, 255, 0.65)",
    dark: "rgba(217, 237, 255, 0.85)",
    light: "rgba(217, 237, 255, 0.5)",
    contrastText: "#111113",
  },
  action: {
    active: "rgba(217, 237, 255, 0.4)",
    hover: "rgba(214, 235, 253, 0.05)",
    selected: "#2E2F33",
    disabledBackground: "rgba(221, 234, 248, 0.06)",
    disabled: "rgba(217, 237, 255, 0.25)",
    focus: "rgba(100, 115, 129, 0.12)",
  },
  text: {
    primary: "#EDEDED",
    secondary: "rgba(217, 237, 255, 0.65)",
    disabled: "rgba(217, 237, 255, 0.37)",
  },
  info: {
    main: "#0288D1",
    dark: "#01579B",
    light: "#03A9F4",
    contrastText: "#FFFFFF",
  },
  error: {
    main: "#D32F2F",
    dark: "#C62828",
    light: "#EF5350",
    contrastText: "#FFFFFF",
  },
  warning: {
    main: "#ED6C02",
    dark: "#E65100",
    light: "#FF9800",
    contrastText: "#FFFFFF",
  },
  success: {
    main: "#2E7D32",
    dark: "#1B5E20",
    light: "#4CAF50",
    contrastText: "#FFFFFF",
  },
  background: {
    default: "#111113",
    paper: "#111113",
    grey: "#171719",
  },
  divider: "rgba(214, 235, 253, 0.1)",
  common: {
    black: "#000000",
    white: "#FFFFFF",
  },
  tooltip: {
    fill: "#23343B",
  },
  DataGrid: {
    headerBg: "#171719",
  },
  subtleSurfaceBg: "#2E2F33",
};

const commonComponents: ThemeOptions["components"] = {
  MuiAlert: {
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        const borderColor = ownerState.severity ? theme.palette[ownerState.severity].main : undefined;
        return {
          ...(borderColor &&
            ownerState.variant === "standard" && {
              border: `1px solid ${alpha(borderColor, 0.5)}`,
            }),
        };
      },
      message: ({ theme }) => ({ ...theme.typography.body1 }),
    },
    defaultProps: {
      iconMapping: {
        warning: React.createElement(WarningAmberRoundedIcon),
        success: React.createElement(CheckCircleOutlineRoundedIcon),
        error: React.createElement(ErrorOutlineRoundedIcon),
      },
    },
  },
  MuiAlertTitle: {
    styleOverrides: {
      root: {
        fontSize: 14,
      },
    },
  },
  MuiButtonGroup: {
    defaultProps: {
      size: "small",
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      size: "small",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: "none",
        variants: [
          {
            props: { variant: "outlined", color: "secondary" },
            style: {
              borderColor: theme.palette.divider,
              ":hover": {
                borderColor: theme.palette.divider,
              },
              "&.Mui-disabled": {
                borderColor: theme.palette.divider,
              },
            },
          },
        ],
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      colorPrimary: ({ theme }) => ({
        color: theme.palette.action.active,
      }),
      root: {
        ":hover": {
          borderRadius: "8px",
        },
        ".MuiTouchRipple-root .MuiTouchRipple-child": {
          borderRadius: "8px",
        },
      },
    },
  },
  MuiRadio: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      colorPrimary: ({ theme }) => ({
        color: theme.palette.action.active,
      }),
    },
  },
  MuiSwitch: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      track: ({ theme }) => ({
        backgroundColor: theme.palette.secondary.main,
      }),
    },
  },
  MuiChip: {
    defaultProps: {
      size: "small",
    },
  },
  MuiToggleButtonGroup: {
    defaultProps: {
      size: "small",
    },
  },
  MuiToggleButton: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: (theme) => ({
        textTransform: "none",
        color: theme.theme.palette.secondary.main,
        "&.Mui-selected": {
          backgroundColor: alpha(theme.theme.palette.text.primary, 0.04),
        },
      }),
      sizeSmall: {
        padding: "4px 10px",
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: "36px",
        borderBottom: "1px solid",
        borderColor: theme.palette.divider,
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: "none",
        padding: "3px 16px",
        minHeight: "36px",
      },
    },
  },
  MuiInputBase: {
    defaultProps: {
      size: "small",
    },
  },
  MuiInput: {
    defaultProps: {
      size: "small",
    },
  },
  MuiSelect: {
    defaultProps: {
      size: "small",
      IconComponent: ArrowDropDownRoundedIcon,
    },
    styleOverrides: {
      root: {
        "& .MuiSelect-standard ~ .MuiSelect-icon": {
          top: "calc(50% - 12px)",
        },
      },
    },
  },
  MuiListItemText: {
    defaultProps: {
      slotProps: {
        primary: {
          variant: "body1",
        },
      },
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      size: "small",
      popupIcon: React.createElement(ArrowDropDownRoundedIcon),
    },
    styleOverrides: {
      root: {
        "& .MuiInputLabel-filled.MuiInputLabel-shrink": {
          transform: "translate(12px, 0px) scale(0.75)",
        },
      },
    },
  },
  MuiInputLabel: {
    defaultProps: {
      size: "small",
    },
  },
  MuiTextField: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: {
        "& .MuiInputLabel-filled.MuiInputLabel-shrink": {
          transform: "translate(12px, -1px) scale(0.75)",
        },
      },
    },
  },
  MuiFormControl: {
    defaultProps: {
      size: "small",
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.palette.error.main,
      }),
    },
  },
  MuiIconButton: {
    defaultProps: {
      color: "secondary",
      size: "small",
    },
    styleOverrides: {
      root: {
        borderRadius: "8px",
        ".MuiTouchRipple-root .MuiTouchRipple-child": {
          borderRadius: "8px",
        },
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: ({ theme }) => ({
        variants: [
          {
            props: { color: "default" },
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.secondary.main,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[2],
              ":hover": {
                backgroundColor: theme.palette.background.grey,
              },
            },
          },
        ],
      }),
    },
  },
  MuiSvgIcon: {
    defaultProps: {
      fontSize: "small",
    },
    styleOverrides: {
      // Exposes the theme-driven subtle surface background as a CSS custom property so
      // icon SVGs can reference `fill: var(--subtle-surface-bg)` and follow light/dark.
      root: ({ theme }) => ({
        "--subtle-surface-bg": theme.palette.subtleSurfaceBg,
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-selected": {
          color: theme.palette.primary.main,
          ".MuiTypography-root:not(:where(.MuiChip-root *))": {
            color: theme.palette.primary.main,
          },
        },
      }),
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&:last-child": {
          paddingBottom: theme.spacing(2),
        },
      }),
    },
  },
  MuiTable: {
    defaultProps: {
      size: "small",
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.divider,
        ...theme.typography.body1,
      }),
      head: ({ theme }) => ({
        ...theme.typography.caption,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.grey,
      }),
    },
  },
  MuiAvatar: {
    styleOverrides: {
      colorDefault: ({ theme }) => ({
        backgroundColor: theme.palette.action.selected,
        color: theme.palette.text.secondary,
      }),
    },
  },
  MuiBadge: {
    styleOverrides: {
      colorSecondary: ({ theme }) => ({
        backgroundColor: theme.palette.action.selected,
        color: theme.palette.text.secondary,
      }),
      colorError: ({ theme }) => ({
        backgroundColor: "#FDEDED",
        color: theme.palette.error.main,
      }),
    },
  },
  MuiSlider: {
    defaultProps: {
      size: "small",
    },
  },
  MuiPagination: {
    defaultProps: {
      size: "small",
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: "8px",
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      rounded: {
        borderRadius: "8px",
        ":first-of-type": {
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
        },
        ":last-of-type": {
          borderBottomLeftRadius: "8px",
          borderBottomRightRadius: "8px",
        },
        ":before": {
          left: "8px",
          right: "8px",
        },
        "& .MuiAccordionSummary-root": {
          borderRadius: "8px",
        },
      },
    },
  },
  MuiDateField: {
    defaultProps: {
      size: "small",
    },
  },
  MuiTimeField: {
    defaultProps: {
      size: "small",
    },
  },
  MuiSingleInputDateRangeField: {
    defaultProps: {
      size: "small",
    },
  },
  MuiPickersTextField: {
    defaultProps: {
      size: "small",
    },
  },
  MuiTooltip: {
    defaultProps: {
      // An absolute popper poking past the viewport grows the document and summons a window scrollbar.
      slotProps: { popper: { popperOptions: { strategy: "fixed" } } },
    },
    styleOverrides: {
      tooltip: ({ theme }) => ({ backgroundColor: theme.palette.tooltip.fill }),
      arrow: ({ theme }) => ({ color: theme.palette.tooltip.fill }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: "8px",
      },
      bar: {
        borderRadius: "8px",
      },
    },
  },
  MuiTimelineConnector: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.divider,
      }),
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: 0,
        ...theme.typography.body1,
        ".MuiDataGrid-toolbarContainer": {
          backgroundColor: theme.palette.background.default,
        },
        ".MuiDataGrid-columnHeaders": {
          borderColor: theme.palette.divider,
          "& .MuiDataGrid-columnHeader:focus-within": {
            outline: "none",
          },
        },
        ".MuiDataGrid-columnHeader": {
          "&.MuiDataGrid-columnHeader--withLeftBorder": {
            borderLeftColor: theme.palette.divider,
          },
          "&.MuiDataGrid-columnHeader--withRightBorder": {
            borderRightColor: theme.palette.divider,
          },
          "&.MuiDataGrid-withBorderColor": {
            borderColor: theme.palette.divider,
          },

          "&:focus": {
            outline: "none",
          },
        },
        ".MuiDataGrid-columnHeaderTitle": {
          ...theme.typography.caption,
          color: theme.palette.text.secondary,
        },
        ".MuiDataGrid-row": {
          "&.Mui-hovered:not(.Mui-selected)": {
            backgroundColor: theme.palette.action.hover,
          },
          "&:hover, &.Mui-hovered": {
            ".MuiDataGrid-cell.grid-row-actions": {
              button: {
                visibility: "visible",
              },
              a: {
                visibility: "visible",
              },
            },
            ".highlighted-action": {
              color: theme.palette.primary.main,
            },
          },
          ".MuiDataGrid-cell": {
            outline: "none",
            borderTopColor: theme.palette.divider,
            display: "flex",
            alignItems: "center",

            "&.MuiDataGrid-cell--withLeftBorder": {
              borderLeftColor: theme.palette.divider,
            },
            "&.MuiDataGrid-cell--withRightBorder": {
              borderRightColor: theme.palette.divider,
            },
          },
          "&.MuiDataGrid-row--firstVisible": {
            ".MuiDataGrid-cell": {
              borderTopColor: "transparent",
            },
          },
        },
        ".MuiDataGrid-cell": {
          "&.grid-row-actions": {
            button: {
              visibility: "hidden",
            },
            a: {
              visibility: "hidden",
            },
          },
        },
        ".MuiDataGrid-overlayWrapperInner": {
          position: "absolute",
        },
      }),
    },
    defaultProps: {
      // Without it the grid probes `new Function()` on mount, which the CSP without 'unsafe-eval' reports.
      disableEval: true,
      slots: {
        groupingCriteriaCollapseIcon: CollapseIcon,
        groupingCriteriaExpandIcon: ExpandIcon,
        treeDataCollapseIcon: CollapseIcon,
        treeDataExpandIcon: ExpandIcon,
        detailPanelCollapseIcon: CollapseIcon,
        detailPanelExpandIcon: ExpandIcon,
      },
    },
  },
  MuiChartsAxis: {
    styleOverrides: {
      root: ({ theme }) => ({
        ".MuiChartsAxis-line": { stroke: theme.palette.divider },
        ".MuiChartsAxis-tick": { stroke: theme.palette.divider },
        ".MuiChartsAxis-tickLabel": { fill: theme.palette.text.secondary },
      }),
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
      }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.subtitle2,
      }),
    },
  },
};

const darkhemeComponentOverrides: ThemeOptions["components"] = {
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        border: "rgba(0, 168, 102, 0.5)",
      },
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: {
        border: "rgba(0, 168, 102, 0.5)",
      },
    },
  },
};

export const baseTypography: TypographyVariantsOptions = {
  h1: { fontSize: "2rem", fontWeight: 600, fontFamily: "Roboto", lineHeight: "120%", letterSpacing: "-0.5px" },
  h2: { fontSize: "1.75rem", fontWeight: 600, fontFamily: "Roboto", lineHeight: "120%", letterSpacing: "-0.5px" },
  h3: { fontSize: "1.5rem", fontWeight: 600, fontFamily: "Roboto", lineHeight: "120%" },
  h4: { fontSize: "1.25rem", fontWeight: 600, fontFamily: "Roboto", lineHeight: "120%", letterSpacing: "0.25px" },
  h5: { fontSize: "1.125rem", fontWeight: 600, fontFamily: "Roboto", lineHeight: "120%" },
  h6: { fontSize: "0.9375rem", fontWeight: 600, fontFamily: "Roboto", lineHeight: "130%", letterSpacing: "0.15px" },
  subtitle1: {
    fontSize: "0.875rem",
    fontWeight: 500,
    fontFamily: "Roboto",
    lineHeight: "140%",
    letterSpacing: "0.15px",
  },
  subtitle2: {
    fontSize: "0.8125rem",
    fontWeight: 500,
    fontFamily: "Roboto",
    lineHeight: "140%",
    letterSpacing: "0.1px",
  },
  body1: { fontSize: "0.8125rem", fontWeight: 400, fontFamily: "Roboto", lineHeight: "140%", letterSpacing: "0.15px" },
  body2: { fontSize: "0.75rem", fontWeight: 400, fontFamily: "Roboto", lineHeight: "140%", letterSpacing: "0.17px" },
  caption: { fontSize: "0.75rem", fontWeight: 400, fontFamily: "Roboto", lineHeight: "120%", letterSpacing: "0.4px" },
  button: { fontSize: "0.8125rem", fontWeight: 500, fontFamily: "Roboto", lineHeight: "24px" },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 400,
    fontFamily: "Roboto",
    lineHeight: "120%",
    letterSpacing: "1px",
    textTransform: "uppercase",
  },
};

const narrowScreen = "@media (max-width: 579px)";

const typography: TypographyVariantsOptions = {
  ...baseTypography,
  h6: { ...baseTypography.h6, [narrowScreen]: { fontSize: "1.063rem" } },
  subtitle1: { ...baseTypography.subtitle1, [narrowScreen]: { fontSize: "1rem" } },
  subtitle2: { ...baseTypography.subtitle2, [narrowScreen]: { fontSize: "0.9375rem" } },
  body1: { ...baseTypography.body1, [narrowScreen]: { fontSize: "0.9375rem" } },
  body2: { ...baseTypography.body2, [narrowScreen]: { fontSize: "0.875rem" } },
  button: { ...baseTypography.button, [narrowScreen]: { fontSize: "0.9375rem" } },
};

const breakpoints: ThemeOptions["breakpoints"] = {
  values: {
    xs: 0,
    sm: 579,
    md: 768,
    lg: 1024,
    xl: 1600,
  },
};

export const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: "light",
    ...lightPaletteColors,
  },
  components: {
    ...commonComponents,
  },
  typography,
  breakpoints,
};

export const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: "dark",
    ...darkPaletteColors,
  },
  components: {
    ...commonComponents,
    ...darkhemeComponentOverrides,
  },
  typography,
  breakpoints,
};
